import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import { IconRobot, IconCheck } from '@tabler/icons-react';
import {
    useGetChatbotsQuery,
    useCreateChatbotMutation,
    useAddDocumentsToChatbotMutation
} from 'store/slices/apiSlice';

const AddToChatbotDialog = ({ open, onClose, files, onAdd }) => {
    const [selectedOption, setSelectedOption] = useState('existing');
    const [selectedChatbotId, setSelectedChatbotId] = useState('');
    const [newChatbotName, setNewChatbotName] = useState('');
    const [newChatbotDescription, setNewChatbotDescription] = useState('');
    const [llmModel, setLlmModel] = useState('ollama:llama3');
    const [error, setError] = useState(null);

    const { data: chatbots = [], isLoading: loadingChatbots } = useGetChatbotsQuery(undefined, { skip: !open });
    const [createChatbot, { isLoading: creating }] = useCreateChatbotMutation();
    const [addDocuments, { isLoading: adding }] = useAddDocumentsToChatbotMutation();

    const loading = creating || adding;
    const chatbotsList = Array.isArray(chatbots) ? chatbots : (chatbots?.data ?? []);

    const handleAdd = async () => {
        if (selectedOption === 'existing' && !selectedChatbotId) {
            setError('Vui lòng chọn một chatbot');
            return;
        }
        if (selectedOption === 'new' && !newChatbotName.trim()) {
            setError('Vui lòng nhập tên chatbot');
            return;
        }

        setError(null);
        const fileIds = files.map((f) => f.id || f);

        try {
            if (selectedOption === 'new') {
                const res = await createChatbot({
                    name: newChatbotName,
                    description: newChatbotDescription || undefined,
                    llm_model: llmModel,
                    embedding_model: 'ollama:nomic-embed-text',
                    vector_store_type: 'chromadb'
                }).unwrap();
                const chatbotId = res?.id ?? res?.data?.id;
                if (fileIds.length > 0 && chatbotId) {
                    await addDocuments({ chatbotId, fileIds }).unwrap();
                }
                onAdd?.({ action: 'create_and_add', chatbotId, chatbotName: newChatbotName, fileIds });
            } else {
                await addDocuments({ chatbotId: selectedChatbotId, fileIds }).unwrap();
                onAdd?.({ action: 'add_to_existing', chatbotId: selectedChatbotId, fileIds });
            }
            handleClose();
        } catch (err) {
            const msg =
                err?.data?.detail ?? err?.data?.message ?? err?.message ?? 'Có lỗi xảy ra khi thêm documents vào chatbot';
            setError(Array.isArray(msg) ? msg.join(', ') : msg);
        }
    };

    const handleClose = () => {
        setSelectedOption('existing');
        setSelectedChatbotId('');
        setNewChatbotName('');
        setNewChatbotDescription('');
        setLlmModel('ollama:llama3');
        setError(null);
        onClose?.();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconRobot size={24} />
                    <Typography variant="h6">Thêm Documents vào Chatbot</Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                        Đang chọn <strong>{files.length}</strong> file(s) để thêm vào chatbot với RAG
                    </Typography>
                    {files.length > 0 && files.length <= 5 && (
                        <Box sx={{ mt: 1, pl: 1 }}>
                            {files.map((file, idx) => (
                                <Typography
                                    key={idx}
                                    variant="caption"
                                    display="block"
                                    color="textSecondary"
                                    sx={{ fontSize: '0.75rem' }}
                                >
                                    • {file.filename || file}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                    <FormLabel component="legend">Chọn chatbot</FormLabel>
                    <RadioGroup
                        value={selectedOption}
                        onChange={(e) => setSelectedOption(e.target.value)}
                    >
                        <FormControlLabel value="existing" control={<Radio />} label="Chọn chatbot có sẵn" />
                        <FormControlLabel value="new" control={<Radio />} label="Tạo chatbot mới" />
                    </RadioGroup>
                </FormControl>

                {selectedOption === 'existing' ? (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Chatbot</InputLabel>
                        <Select
                            value={selectedChatbotId}
                            onChange={(e) => setSelectedChatbotId(e.target.value)}
                            label="Chatbot"
                            disabled={loadingChatbots}
                        >
                            <MenuItem value="" disabled>
                                {loadingChatbots ? 'Đang tải...' : 'Không có chatbot nào'}
                            </MenuItem>
                            {chatbotsList.map((chatbot) => (
                                <MenuItem key={chatbot.id} value={chatbot.id}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: '100%'
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body2">{chatbot.name}</Typography>
                                            {chatbot.description && (
                                                <Typography variant="caption" color="textSecondary">
                                                    {chatbot.description}
                                                </Typography>
                                            )}
                                        </Box>
                                        {chatbot.llm_model && (
                                            <Chip
                                                label={chatbot.llm_model}
                                                size="small"
                                                sx={{ ml: 1, fontSize: '0.65rem' }}
                                            />
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    <Box>
                        <TextField
                            fullWidth
                            label="Tên Chatbot"
                            value={newChatbotName}
                            onChange={(e) => setNewChatbotName(e.target.value)}
                            sx={{ mb: 2 }}
                            required
                            placeholder="Ví dụ: Customer Support Bot"
                        />
                        <TextField
                            fullWidth
                            label="Mô tả (tùy chọn)"
                            value={newChatbotDescription}
                            onChange={(e) => setNewChatbotDescription(e.target.value)}
                            sx={{ mb: 2 }}
                            multiline
                            rows={2}
                            placeholder="Mô tả về mục đích của chatbot này"
                        />
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>LLM Model</InputLabel>
                            <Select
                                value={llmModel}
                                onChange={(e) => setLlmModel(e.target.value)}
                                label="LLM Model"
                            >
                                <MenuItem value="ollama:llama3">Ollama: Llama 3</MenuItem>
                                <MenuItem value="ollama:mistral">Ollama: Mistral</MenuItem>
                                <MenuItem value="ollama:codellama">Ollama: CodeLlama</MenuItem>
                                <MenuItem value="gpt-4">GPT-4</MenuItem>
                                <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
                                <MenuItem value="claude-3-opus">Claude 3 Opus</MenuItem>
                                <MenuItem value="claude-3-sonnet">Claude 3 Sonnet</MenuItem>
                            </Select>
                        </FormControl>
                        <Box sx={{ p: 1.5, bgcolor: 'info.light', borderRadius: 1, mb: 2 }}>
                            <Typography variant="caption" color="textSecondary">
                                <strong>RAG sẽ được tự động kích hoạt:</strong> Documents sẽ được vectorize và index để
                                chatbot có thể truy xuất thông tin từ chúng khi trả lời câu hỏi.
                            </Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Hủy
                </Button>
                <Button
                    onClick={handleAdd}
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} /> : <IconCheck size={18} />}
                    disabled={
                        loading ||
                        (selectedOption === 'existing' && !selectedChatbotId) ||
                        (selectedOption === 'new' && !newChatbotName.trim())
                    }
                >
                    {selectedOption === 'new' ? 'Tạo & Thêm' : 'Thêm Documents'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddToChatbotDialog;
